import { capitalize } from '@mui/material';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';

import i18n, { i18nLanguage, LanguageMap } from 'src/i18n';

import 'dayjs/locale/en-gb';

dayjs.extend(relativeTime);
dayjs.extend(isBetween);

const numericDateFormatter = new Intl.DateTimeFormat('no-NB', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('no-NB', {
  hour: 'numeric',
  minute: 'numeric',
});

const relativeTimeFormatter = new Intl.RelativeTimeFormat(i18n.language, {
  numeric: 'auto',
});

const dateTimeFormatter = new Intl.DateTimeFormat(i18n.language, {
  month: 'short',
  hour: 'numeric',
  minute: 'numeric',
  year: 'numeric',
  day: 'numeric',
  hourCycle: 'h23',
});

const digitalDateTimeFormatter = new Intl.DateTimeFormat('no-NB', {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hourCycle: 'h23',
});

const fullDateTimeFormatter = new Intl.DateTimeFormat(i18n.language, {
  dateStyle: 'full',
  hourCycle: 'h23',
  timeStyle: 'medium',
});

type Division = {
  amount: number;
  name: Intl.RelativeTimeFormatUnit;
};

const divisions: Division[] = [
  { amount: 60, name: 'seconds' },
  { amount: 60, name: 'minutes' },
  { amount: 24, name: 'hours' },
  { amount: 7, name: 'days' },
  { amount: 4.34524, name: 'weeks' },
  { amount: 12, name: 'months' },
  { amount: Number.POSITIVE_INFINITY, name: 'years' },
];

export const formatTimeAgo = (dateString: string) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  let duration = (date.getTime() - new Date().getTime()) / 1000;

  for (let i = 0; i <= divisions.length; i++) {
    const division = divisions[i];
    if (Math.abs(duration) < division.amount)
      return relativeTimeFormatter.format(Math.round(duration), division.name);

    duration /= division.amount;
  }
};

export const formatDate = (
  date: Date | number | string,
  /**
   * If value is `minimal`, a short, numeric representation will be returned.
   * If value is `full`, a full representation will be returned.
   * If value is `default` or `undefined`, something
   */
  scope: 'default' | 'digital' | 'full' | 'minimal' = 'default'
) => {
  if (date)
    try {
      return (
        scope === 'minimal'
          ? numericDateFormatter
          : scope === 'full'
            ? fullDateTimeFormatter
            : scope === 'digital'
              ? digitalDateTimeFormatter
              : dateTimeFormatter
      ).format(typeof date === 'string' ? new Date(date) : date);
    } catch {
      return '';
    }
  return '';
};

export const formatTime = (dateString: string) => {
  try {
    return timeFormatter.format(new Date(dateString));
  } catch {
    return 'N/A';
  }
};

export const formatDuration = (fromString: string, toString: string) => {
  const from = new Date(fromString);
  const to = new Date(toString);

  const toPaddedString = (value: number) => (value < 10 ? '0' + value : value.toString());

  const duration = (to.getTime() - from.getTime()) / 1000;

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration - hours * 3600) / 60);
  const seconds = Math.floor(duration - hours * 3600 - minutes * 60);

  return `${toPaddedString(hours)}:${toPaddedString(minutes)}:${toPaddedString(seconds)}`;
};

const localeMap: Record<i18nLanguage, string> = {
  [LanguageMap.enUS]: 'en-gb',
};

export const formatRelativeTime = (dateStr: string) => {
  if (!dateStr) return '';

  const date = dayjs(dateStr);
  const now = dayjs();
  const sixDaysAgo = now.subtract(6, 'day');
  const sixDaysFromNow = now.add(6, 'day');

  if (date.isBetween(sixDaysAgo, sixDaysFromNow, null, '[]')) {
    dayjs.locale(localeMap[i18n.language as i18nLanguage] || 'en-gb');
    return capitalize(date.from(now));
  }

  return formatDate(dateStr);
};
